// @ts-nocheck
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { debounce, forEach, keys } from 'lodash'

import { colors } from '@lmc/common/constants'
import { getJSON } from '../../../../common/services/api'
import { HelpLink, Spinner } from '../../../generic'
import {
  EmarMedicationResidentInfo,
  EmarMedicationSearchListItem,
} from '../components'
import ListHeaderSearch from '../../../../LEGACY/ListHeaderSearch'
import { EmarCustomMedNameField } from '../fields'
import { Text } from '@lmc/ui-kit'
import { ButtonOLD } from '@lmc/ui-kit'

const FETCH_DEBOUNCE = 500

/**
@description Renders the first step in the Add eMar Medication modal

@param {object} fields. The form fields.

@param {object} formState. The form state.

@param {function} goToNextStep. Fires when the Next Step button is pressed. 
Takes the user to the next modal step

@param {object} resident. The resident.

@param {function} setFormField. Sets the form fields. The field for this
component is medication. 

@param {function} setFormState. Sets the form state. Used in this component
to store the medication name.

@returns {function} AddMedNameStep.
*/

export default class AddMedNameStep extends Component {
  constructor(props) {
    super(props)
    this.myRef = React.createRef() // Create a ref object
  }

  state = {
    customEntry: false,
    isLoading: false,
    requestCount: 0,
    search: '',
  }

  componentDidMount() {
    const { mixpanel } = this.props
    if (mixpanel) mixpanel.track('CO_emar_modal_open')
  }

  debounceFetchMeds = debounce(
    (query) => {
      const { mixpanel } = this.props
      const requestCount = this.state.requestCount + 1
      this.setState(
        { requestCount, isLoading: true, medSearchResults: [] },
        async () => {
          try {
            const { result } = await getJSON({
              url: `/api/list/nhs-branded-meds?name=${query}`,
            })
            if (requestCount === this.state.requestCount) {
              this.setState({
                medSearchResults: result,
                isLoading: false,
              })
            }
          } catch (e) {
            console.log(e)
            this.setState({
              isLoading: false,
            })
          }
        }
      )

      if (mixpanel) mixpanel.track('CO_emar_modal_med_search')
    },
    FETCH_DEBOUNCE,
    {}
  )

  handleSearchChange = (e) => {
    const newValue = e.target.value
    this.setState({ search: newValue, isLoading: true })
    this.debounceFetchMeds(newValue)
  }

  handleClickOut = () => {
    this.setState({
      medSearchResults: null,
      isLoading: false,
    })
  }

  handleSearchClear = () => {
    const { clear, resident, setFormField } = this.props

    this.setState({ search: '', medSearchResults: null })
    clear()
    setFormField({ key: 'resident', value: resident.id })
  }

  handleAddCustomMed = () => {
    const { clear, resident, setFormField } = this.props

    this.setState({ customEntry: true, medSearchResults: null })
    clear()
    setFormField({ key: 'resident', value: resident.id })
  }

  setControlledDrugFields = (props) => {
    const { controlledDrug, setFormField, setFormState } = props

    const dependentFields = [
      { key: 'secondSignatureRequired', value: controlledDrug ? true : null },
    ]

    forEach(dependentFields, (field) => {
      setFormField({ key: field.key, value: field.value })
      setFormState({ key: `${field.key}PrePopped`, value: controlledDrug })
    })
  }

  handleSelectMedication = (medication) => {
    const { setFormField, setFormState } = this.props

    forEach(keys(medication), (key) => {
      if (key === 'controlledDrug') {
        this.setControlledDrugFields({
          controlledDrug: medication[key],
          setFormField,
          setFormState,
        })
        setFormField({ key, value: medication[key] })
        setFormState({ key: `${key}PrePopped`, value: !!medication[key] })
        setFormState({ key: `${key}Initial`, value: medication[key] }) // Stores the original nhs value for controlled drug
      } else if (key === 'unit') {
        setFormField({ key: 'quantityType', value: medication[key] })
        setFormField({ key: 'stockQuantityType', value: medication[key] })
        setFormState({ key: `quantityTypePrePopped`, value: !!medication[key] })
        setFormState({ key: `freeTextDose`, value: !medication[key] })
        setFormState({ key: `defaultQuantityType`, value: medication[key] })
      } else if (key !== 'id') {
        setFormField({ key, value: medication[key] })
        setFormState({ key: `${key}PrePopped`, value: !!medication[key] })
      }
    })

    this.setState({ medSearchResults: null })
    setFormState({ key: 'medicationName', value: medication.name })
    setFormField({ key: 'medication', value: medication.id })
  }

  // componentDidMount () {
  //     const { myRef } = this
  //     myRef.current?.scrollIntoView()
  // }

  onNext = () => {
    const { goToNextStep, setFormField, setFormState } = this.props
    const { customEntry, search } = this.state

    if (customEntry) {
      setFormField({ key: 'customName', value: search })
      setFormState({ key: 'medicationName', value: search })
    }

    goToNextStep()
  }

  // Render functions

  renderResidentInfo = () => {
    const { resident } = this.props
    return <EmarMedicationResidentInfo resident={resident} />
  }

  // Render Search Box functions

  displayNoResults = () => (
    <div style={styles.noResultsContainer}>
      <h2 style={styles.noResultsTitle}>No results</h2>
      <div style={styles.noResultsText}>
        Search tips:
        <ul style={styles.noResultsBulletList}>
          <li style={styles.noResultsBullet}>
            Double check for any spelling mistakes.
          </li>
          <li style={styles.noResultsBullet}>
            Make sure that you've got the right medication name.
          </li>
          <li style={styles.noResultsBullet}>
            If the medication you’re looking for is not a part of the NHS
            database, you can
            <a onClick={() => this.handleAddCustomMed()}>
              {` add a custom medication.`}
            </a>
          </li>
        </ul>
      </div>
      <div style={styles.noResultsHelpLink}>
        <HelpLink supportLink="https://support.logmycare.co.uk/en/collections/2749236-emar" />
      </div>
    </div>
  )

  displayMedicationList = (searchResults) => {
    const { search } = this.state

    if (searchResults.length === 0) return this.displayNoResults()

    return (
      <div>
        {searchResults.map((medication) => (
          <EmarMedicationSearchListItem
            medication={medication}
            onClick={() => this.handleSelectMedication(medication)}
            searchTerm={search}
          />
        ))}
        <div style={styles.addCustomContainer}>
          <EmarMedicationSearchListItem
            medication={{ name: '+ Add custom medication' }}
            onClick={() => this.handleAddCustomMed()}
            searchTerm="none"
          />
        </div>
      </div>
    )
  }

  renderSearchResults = () => {
    const { medSearchResults, isLoading } = this.state

    return (
      <div>
        <div style={styles.searchResultsWrapper}>
          <div style={styles.searchResultsContainer}>
            {isLoading ? (
              <Spinner style={styles.spinner} />
            ) : (
              this.displayMedicationList(medSearchResults)
            )}
          </div>
        </div>
      </div>
    )
  }

  isResultDisplayed = () => {
    const { medSearchResults, search } = this.state
    return medSearchResults && search && search !== ''
  }

  renderSearchBox = () => {
    const {
      formState: { medicationName },
    } = this.props
    const { customEntry } = this.state

    const handleChange = medicationName ? () => {} : this.handleSearchChange
    const inputStyle = medicationName
      ? {
          ...styles.searchInput,
          borderColor: colors.grey,
          cursor: 'default',
        }
      : styles.searchInput

    const value = medicationName || this.state.search
    const isResultDisplayed = this.isResultDisplayed()
    return (
      <div style={styles.searchBoxWrapper}>
        <div style={styles.searchBoxContainer}>
          <Text style={styles.searchBoxTitle} as="h5">
            Name of the medication <span style={{ color: colors.pink }}>*</span>
          </Text>
          {medicationName && <div style={styles.searchBoxLock} />}
          {customEntry ? (
            <EmarCustomMedNameField
              {...this.props}
              onCancel={() => {
                this.setState({ customEntry: false, search: '' })
              }}
              onChange={(entry) => this.setState({ search: entry.value })}
              values={{ medicationName: value }}
            />
          ) : (
            <ListHeaderSearch
              inputStyle={inputStyle}
              handleChange={handleChange}
              handleClear={this.handleSearchClear}
              placeholder="Search by name on the database"
              style={styles.searchBox}
              value={value}
            />
          )}
          {isResultDisplayed && this.renderSearchResults()}
        </div>
        {/* Render a full screen area that can be clicked to clear the result */}
        {isResultDisplayed && (
          <div onClick={this.handleClickOut} style={styles.clickOutArea} />
        )}
      </div>
    )
  }

  renderFooter = () => {
    const { formState } = this.props
    const { customEntry } = this.state

    return (
      <div style={styles.footer}>
        <HelpLink supportLink="https://support.logmycare.co.uk/en/articles/4851795-how-do-i-add-a-medication" />
        <ButtonOLD
          onClick={() => this.onNext()}
          type="primary"
          style={styles.nextStepBtn}
          disabled={!formState.medicationName && !customEntry}
        >
          Next Step
        </ButtonOLD>
      </div>
    )
  }

  render() {
    return (
      <div style={styles.container}>
        {this.renderSearchBox()}
        {this.renderFooter()}
      </div>
    )
  }
}

const styles = {
  addCustomContainer: {
    color: colors.pink,
    fontWeight: 600,
  },
  button: {
    height: 40,
    marginTop: 20,
  },
  clickOutArea: {
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100vh',
    width: '100vw',
  },
  container: {
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto',
    maxWidth: 960,
    padding: '0px 10px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 20,
    alignItems: 'center',
  },
  lmcDivider: {
    height: 2,
    width: '100%',
  },
  noResultsBullet: {
    padding: '3px 0px',
  },
  noResultsBulletList: {
    margin: 0,
    paddingLeft: 19,
  },
  noResultsContainer: {
    padding: '30px 0px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    margin: 'auto',
    maxWidth: 375,
  },
  noResultsHelpLink: {
    paddingTop: 12,
    alignSelf: 'flex-start',
  },
  noResultsText: {
    lineHeight: '22px',
  },
  noResultsTitle: {
    lineHeight: '32px',
    marginBottom: 30,
  },
  searchBox: {
    marginTop: 10,
  },
  searchBoxContainer: {
    borderRadius: 15,
    marginTop: 18,
    marginBottom: 19,
    padding: 16,
    width: '100%',
    position: 'relative',
    background: colors.white,
    zIndex: 2,
  },
  searchBoxLock: {
    height: 39,
    position: 'relative',
    top: 47,
    zIndex: 10,
    marginTop: '-39px',
    marginRight: 25,
  },
  searchBoxTitle: {
    lineHeight: '22px',
  },
  searchBoxWrapper: {
    width: '100%',
    height: 139,
  },
  searchInput: {
    fontSize: 14,
  },
  searchResultsContainer: {
    overflow: 'auto',
    maxHeight: 350,
    borderRadius: 4,
    marginTop: 16,
    border: `1px solid ${colors.grey}`,
    background: `${colors.white}`,
  },
  searchResultsWrapper: {
    position: 'relative',
    background: colors.white,
  },
  spinner: {
    padding: 8,
  },
}
